import { Entypo, FontAwesome, MaterialCommunityIcons } from "@expo/vector-icons";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Platform, Pressable, Text, View } from 'react-native';
import { Title } from "@constants/Themed";
import { useDispatch, useSelector } from 'react-redux';
import { FLIP_MESSAGE_MODAL, HIDE_MESSAGE_MODAL, SET_ACTIVE_WHATSAPP_NUMBER, SHOW_HELP_MODAL, SHOW_MESSAGE_MODAL, TOGGLE_MESSAGE_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import styles from "@stylesheet";
import filter from 'lodash.filter';
import MessageSettings from "./Components/MessageSettings";
import ToolBar from "../ToolBar/ToolBar";
import SocketManager from '@utils/socket';
import { connect } from 'react-redux';
import AvatarHandler from "@components/Global/AvatarHandler/AvatarHandler";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { useTranslation } from "react-i18next";
import { Select, XStack, YStack } from "tamagui";
import ChatContainer from "./Components/ChatContainer";
import { ContextualChat } from "./Components/ContextualChat";
import { LinearGradient } from 'expo-linear-gradient';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import MessageList from "./utils/MessageList";
import { MessageListProvider, useMessageList } from "./Context/MessageListContext";
import webTransition from '@utils/webTransition';

// --- NEW: Import Moti ---
import { MotiView } from 'moti';
import { getConversations } from "@api/whatsappApi";
import { clearBadge } from "./utils/handleMessage";

const OPEN_PANEL_WIDTH = 380;

// WhatsApp Chat Wrapper Component
const WhatsAppChatWrapper: React.FC<{
  conversationId: string;
  recipientId: string;
  recipientName: string;
  businessPhoneNumberId: string;
  contactInfo?: any;
  contactName?: string;
  contactProfilePicture?: string;
  participantPhone?: string;
}> = ({
  conversationId,
  recipientId,
  recipientName,
  businessPhoneNumberId,
  contactInfo,
  contactName,
  contactProfilePicture,
  participantPhone
}) => {
  
  return (
    <ContextualChat
      conversationId={conversationId}
      recipientId={recipientId}
      recipientName={recipientName}
      businessPhoneNumberId={businessPhoneNumberId}
      contactInfo={contactInfo}
      contactName={contactName}
      contactProfilePicture={contactProfilePicture}
      participantPhone={participantPhone}
      initialMessages={[]} // Empty array - ContextualChat will fetch its own messages
      onMessagesChange={(messages) => {
        console.log('📱 Messages updated in ContextualChat:', messages.length);
      }}
    />
  );
};
const CLOSED_PANEL_WIDTH = 70;

// Note: I've removed props that were no longer used after refactoring, like messageFlipRef
const Messages = ({ messages, messageFlip, convoId, statusBarHeight }) => {
  const visibleState = useSelector((state: DefaultRootState) => state.modals.messageModal.modalDisplayed);
  const theme = themeSelector();
  const selectedConversation = useSelector((state: DefaultRootState) => state.modals.messageModal.content);
  
  // Debug selectedConversation

  const { userData } = useSelector((state: DefaultRootState) => state.promiseData.data);
  const { toolbarDisplayed } = useSelector((state: DefaultRootState) => state.modals.toolbar);
  const activeFilter = useSelector((state: DefaultRootState) => state.UI.messageAppFilter);
  const activeWhatsappNumber = useSelector((state: DefaultRootState) => state.UI.messageOptions?.activeWhatsappNumber);
  const availableNumbers = useSelector((state: DefaultRootState) => state.UI.messageOptions?.whatsappNumbers || []);

  const [fullData, setFullData] = useState([]);
  const [isUserOrganisation, setIsUserOrganisation] = useState(true);
  const [data, setData] = useState([]);
  const [isRegexMode, setIsRegexMode] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const userId = userData.id;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // Ensure companyMembers is always an array and properly typed
  type Member = { role?: string;[key: string]: any };
  const initialCompanyMembers = Array.isArray(queryClient.getQueryData(['companyMembers']))
    ? (queryClient.getQueryData(['companyMembers']) as Member[])
    : [];

  const [companyMembers, setCompanyMembers] = useState<Member[]>(initialCompanyMembers);

  const [searchVisible, setSearchVisible] = useState(false);

  const socketManager = new SocketManager('messaging', { autoconnect: false, reconnection: true });
  const socket = socketManager.getSocket(userId);
  const authenticated = useSelector((state: DefaultRootState) => state.promiseData?.isAuthenticated || false);

  const [stateStatus, setStateStatus] = useState("false");
  const { t } = useTranslation();
  const externalFilter = useSelector((state: DefaultRootState) => state.UI.messageExternalFilter);

  // Helper function to extract regex flags from pattern
  const parseRegexPattern = useCallback((pattern) => {
    const flagsMatch = pattern.match(/\/([gimsuxy]*)$/);
    const flags = flagsMatch ? flagsMatch[1] : 'i'; // Default to case-insensitive
    const regexPattern = pattern.replace(/^\/|\/[gimsuxy]*$/g, '');
    return { pattern: regexPattern, flags };
  }, []);

  // Helper function to get searchable text from user object
  const getSearchableText = useCallback((user) => {
    const { name, surname } = user.profile_information || { name: "", surname: "" };
    return {
      name: name || '',
      surname: surname || '',
      fullName: `${name || ''} ${surname || ''}`.trim(),
      email: user.email || '',
      username: user.username || '',
      phone: user.phone || '',
      // Add more searchable fields as needed
    };
  }, []);

  const handleSearch = useCallback((text) => {
    if (!text || text.trim() === '') {
      setData(fullData);
      setIsRegexMode(false);
      setSearchError(null);
      return;
    }

    try {
      // Check if the input is a valid regex pattern
      const isRegexPattern = text.startsWith('/') && (text.match(/\//g) || []).length >= 2;
      setIsRegexMode(isRegexPattern);
      
      if (isRegexPattern) {
        // Parse regex pattern and flags
        const { pattern, flags } = parseRegexPattern(text);
        const regex = new RegExp(pattern, flags);
        
        const filteredData = filter(fullData, user => {
          const searchableFields = getSearchableText(user);
          
          // Test regex against all searchable fields
          return Object.values(searchableFields).some(field => 
            field && regex.test(field)
          );
        });
        
        setData(filteredData);
        setSearchError(null);
      } else {
        // Enhanced string search with fuzzy matching
        const formattedQuery = text?.toLowerCase?.().trim();
        const queryWords = formattedQuery.split(/\s+/);
        
        const filteredData = filter(fullData, user => {
          const searchableFields = getSearchableText(user);
          const searchText = Object.values(searchableFields)
            .join(' ')
            .toLowerCase();
          
          // Check if all query words exist in the search text
          return queryWords.every(word => 
            searchText.includes(word)
          ) || 
          // Or check individual fields for partial matches
          Object.values(searchableFields).some(field => 
            field && field.toLowerCase().includes(formattedQuery)
          );
        });
        
        setData(filteredData);
        setSearchError(null);
      }
    } catch (error) {
      // If regex is invalid, provide specific error message
      const errorMessage = error.message || 'Invalid regex pattern';
      console.warn('Regex search error:', error);
      setSearchError(errorMessage);
      setIsRegexMode(false);
      
      // Fall back to basic string search
      const formattedQuery = text?.toLowerCase?.();
      const filteredData = filter(fullData, user => {
        const { name, surname } = user.profile_information || { name: "", surname: "" };
        return (name?.toLowerCase()?.includes(formattedQuery)) || 
               (surname?.toLowerCase()?.includes(formattedQuery));
      });
      setData(filteredData);
    }
  }, [fullData, parseRegexPattern, getSearchableText]);

  // --- NEW: Simplified flip function ---
  const flip = useCallback(() => {
    dispatch({ type: FLIP_MESSAGE_MODAL });
  }, [dispatch]);

  const panelWidth = visibleState ? 380 : 70;

  const mainContainerStyle = useMemo(() => {
    const baseStyle = {
      position: 'absolute' as const,
      top: 0,
      right: 0,
      height: '100%' as const,
      width: panelWidth,
      zIndex: 1000,
      flexDirection: 'row' as const,
      overflow: 'hidden' as const,
    };
    
    // Add web-specific transition
    if (Platform.OS === 'web') {
      return {
        ...baseStyle,
        ...webTransition('width'),
      };
    }
    
    return baseStyle;
  }, [visibleState, panelWidth]);

  const messagePanelStyle = {
    flex: 1,
    justifyContent: "flex-end" as const,
    elevation: 1,
  };

  return (
    <View style={mainContainerStyle}>
      <MessageListProvider>
        {toolbarDisplayed && (
          <View style={{ zIndex: 10, minWidth: 70, shadowRadius: 20, shadowOpacity: .2 }}>
            <ToolBar />
          </View>
        )}
        <View style={messagePanelStyle}>
          {/* --- NEW: MotiView replaces FlipCard --- */}
          <MotiView
            style={{ flexDirection: 'row', height: '100%', width: panelWidth * 2 }}
            from={{ translateX: 0 }}
            animate={{ translateX: messageFlip ? -panelWidth : 0 }}
            transition={{ type: 'timing', duration: 250 }}
          >
            {/* Panel 1: Message List */}
            <LinearGradient
              colors={['#C8A2C8', '#87CEEB']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                width: panelWidth,
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Floating blur elements for depth */}
              {Platform.OS === 'web' && (
                <>
                  <View style={{
                    position: 'absolute',
                    top: '15%',
                    left: '5%',
                    width: 80,
                    height: 80,
                    backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
                    borderRadius: 40,
                    ...(Platform.OS === 'web' && {
                      filter: 'blur(40px)',
                    }),
                    zIndex: 0,
                  }} />
                  <View style={{
                    position: 'absolute',
                    bottom: '20%',
                    right: '10%',
                    width: 100,
                    height: 100,
                    backgroundColor: theme ? 'rgba(200, 162, 200, 0.25)' : 'rgba(135, 206, 235, 0.2)',
                    borderRadius: 50,
                    ...(Platform.OS === 'web' && {
                      filter: 'blur(50px)',
                    }),
                    zIndex: 0,
                  }} />
                </>
              )}
              <View style={{
                flex: 1,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(20px)',
                }),
                zIndex: 1,
              }}>
                <View style={{ flexDirection: 'row', zIndex: 3, padding: 12, alignItems: 'center', elevation: 1, maxHeight: 70, minHeight: 50, backgroundColor: 'transparent' }}>
                {visibleState ? (
                  <View style={{ flexDirection: 'row', alignSelf: 'stretch', flex: 1, alignItems: 'center' }}>
                    <Title selectable={false} style={{ flex: 1, color: theme ? '#333333' : 'white', fontWeight: '400' }}>{t('messageHeader')}</Title>
                    <Pressable style={{ marginHorizontal: 10 }} onPress={() => dispatch({ type: TOGGLE_MESSAGE_MODAL, payload: null })}>
                      <MaterialCommunityIcons name={"message-settings-outline"} size={20} color={theme ? '#333333' : "white"} />
                    </Pressable>
                    <Pressable style={{ marginHorizontal: 10 }} onPress={() => { }}>
                      <Entypo name={"popup"} size={20} color={theme ? '#333333' : "white"} />
                    </Pressable>
                  </View>
                ) : (
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Pressable id="tutorial-messages-button" onPress={() => dispatch({ type: TOGGLE_MESSAGE_MODAL, payload: null })}>
                      <MaterialCommunityIcons name={"message-settings-outline"} size={24} color={theme ? '#333333' : "white"} />
                    </Pressable>
                  </View>
                )}
              </View>
              {
                activeFilter !== 'default' && (
                  <YStack>
                    <XStack style={[styles.centerEverything, { padding: 10 }]}>
                      <FontAwesome name={activeFilter} size={24} color="#25D366" style={{ marginRight: 8 }} />
                      {visibleState && (
                        <Text style={{ fontWeight: 'bold', color: theme ? "#333333" : "white" }}>
                          {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
                        </Text>
                      )}
                    </XStack>
                    {activeFilter === 'whatsapp' && availableNumbers.length > 0 && (
                      <XStack marginHorizontal={10}>
                        <Select
                          value={activeWhatsappNumber || ''}
                          onValueChange={(value) => {
                            queryClient.invalidateQueries({ queryKey: ['all-users'] })
                            dispatch({ type: SET_ACTIVE_WHATSAPP_NUMBER, payload: value });
                          }}
                        >
                          <Select.Trigger paddingHorizontal={10}>
                            <Select.Value placeholder="Select WhatsApp Number" />
                          </Select.Trigger>
                          <Select.Content>
                            <Select.ScrollUpButton />
                            <Select.Viewport paddingHorizontal={10}>
                              {availableNumbers.map((number, index) => (
                                <Select.Item key={number} value={number} index={index}>
                                  <Select.ItemText>{number}</Select.ItemText>
                                </Select.Item>
                              ))}

                            </Select.Viewport>
                            <Select.ScrollDownButton />
                          </Select.Content>
                        </Select>
                      </XStack>
                    )}
                  </YStack>
                )
              }
              <MessageSettings style={{ flex: 1 }} />
              <View style={{ flex: 20, }}>
                <MessageList
                  itemMinWidth={300}
                  isUserOrganisation={isUserOrganisation}
                  // setVisibleState={() => { }}
                  flipCard={flip}
                  handleOpen={() => {
                    dispatch({ type: SHOW_MESSAGE_MODAL })
                  }}
                  handleSearch={handleSearch}
                  setSearchVisible={setSearchVisible}
                  socket={socket}
                  userId={userId}
                  companyMembers={[]}
                  fullData={[]}
                  setIsUserOrganisation={setIsUserOrganisation}
                  setData={setData}
                    clearBadge={(userId: string) => {
                      if (!userId) {
                        console.warn('🔘 Messages: clearBadge called without userId');
                        return;
                      }

                      console.log('🔘 Messages: Clearing badge for user/conversation:', userId, 'activeFilter:', activeFilter);

                      // Determine effective filter (same logic as LazyMessages)
                      const effectiveFilter = activeFilter || 'default';

                      // Update React Query cache for all-users query
                      // Update for both filter states AND effectiveFilter to ensure badge is cleared regardless of active filter
                      const filtersToUpdate = [
                        { filter: activeFilter, externalFilter: true },
                        { filter: activeFilter, externalFilter: false },
                        { filter: effectiveFilter, externalFilter: true },
                        { filter: effectiveFilter, externalFilter: false },
                        // Also check WhatsApp-specific filters
                        { filter: 'whatsapp', externalFilter: false },
                        { filter: 'all', externalFilter: false },
                      ];

                      // Remove duplicates
                      const uniqueFilters = filtersToUpdate.filter((f, index, self) =>
                        index === self.findIndex((t) => t.filter === f.filter && t.externalFilter === f.externalFilter)
                      );

                      uniqueFilters.forEach(({ filter: filterValue, externalFilter: extFilter }) => {
                        queryClient.setQueryData(
                          ['all-users', { filter: filterValue, externalFilter: extFilter, page: 1, limit: 50 }],
                          (oldData: any[] | undefined) => {
                            if (!oldData || !Array.isArray(oldData)) {
                              return oldData; // Skip if no data for this filter
                            }

                            // Find and update the user/conversation that matches the userId
                            const updatedData = oldData.map(user => {
                              // Check multiple possible ID fields to match the user/conversation
                              // For WhatsApp: check conversationId, _id, id
                              // For regular messages: check _id, user._id, id, user.id
                              const userMatchId = user?.conversationId?.toString() ||
                                user?._id?.toString() ||
                                user?.user?._id?.toString() ||
                                user?.id?.toString() ||
                                user?.user?.id?.toString();

                              if (userMatchId === userId.toString()) {
                                console.log('✅ Clearing badge in cache for user/conversation:', {
                                  userId: userMatchId,
                                  conversationId: user?.conversationId,
                                  oldBadge: user.badge || user.unreadCount || 0,
                                  filter: filterValue,
                                  externalFilter: extFilter
                                });
                                return {
                                  ...user,
                                  badge: 0,
                                  unreadCount: 0, // Also clear unreadCount if it exists
                                };
                              }
                              return user;
                            });

                            return updatedData;
                          }
                        );
                      });

                      // Also update local state if it exists (for backward compatibility)
                      if (data && setData && Array.isArray(data)) {
                        const updatedLocalData = data.map((item) => {
                          const itemMatchId = item?.conversationId?.toString() ||
                            item?._id?.toString() ||
                            item?.user?._id?.toString() ||
                            item?.id?.toString() ||
                            item?.user?.id?.toString();

                          if (itemMatchId === userId.toString()) {
                            return { ...item, badge: 0, unreadCount: 0 };
                          }
                          return item;
                        });
                        setData(updatedLocalData);
                      }
                    }}
                  selectedConversation={selectedConversation}
                  closeMessage={() => {
                    dispatch({ type: HIDE_MESSAGE_MODAL })
                  }}
                />
              </View>
              </View>
            </LinearGradient>

            {/* Panel 2: Chat Container */}
            <View style={{ width: panelWidth, flex: 1, height: '100%', backgroundColor: theme ? 'white' : '#333333' }}>
              <View style={{ flexDirection: 'row', zIndex: 3, padding: 12, alignItems: 'center', elevation: 1, maxHeight: 70, backgroundColor: theme ? 'white' : '#333333' }}>
                <Pressable onPress={() => {
                  // If we're in chat view (messageFlip is true), go back to conversation list
                  // Otherwise, hide the modal
                  if (messageFlip) {
                    // Go back to conversation list first
                    dispatch({ type: FLIP_MESSAGE_MODAL, payload: null });
                  } else {
                    // We're already in list view, hide the modal
                    dispatch({ type: HIDE_MESSAGE_MODAL });
                  }
                }} style={{ paddingRight: 15 }}>
                  <MaterialCommunityIcons name={"arrow-left"} size={30} color={theme ? '#333333' : "white"} />
                </Pressable>
                {selectedConversation && (
                  <>
                    <AvatarHandler avatar={selectedConversation.avatar || selectedConversation.avatar?.img} />
                    <View style={{ flex: 1, marginLeft: 10 }}>
                      <Title numberOfLines={1} style={{ color: theme ? '#333333' : 'white' }}>
                        {selectedConversation.name} {selectedConversation.surname}
                      </Title>
                    </View>
                  </>
                )}
              </View>
              <View style={{ flex: 1, overflow: 'hidden' }}>
                {selectedConversation && (
                  activeFilter === 'whatsapp' ? (
                    (() => {
                      const conversationId = selectedConversation.conversationId || selectedConversation._id || selectedConversation.id;

                      // Extract customer phone number from participants (exclude businessPhoneNumberId)
                      // The participants array contains [businessPhoneNumberId, customerPhoneNumber]
                      // We need to find the participant that is NOT the businessPhoneNumberId
                      const businessPhoneNumberIdForRecipient = selectedConversation.businessPhoneNumberId || activeWhatsappNumber;
                      const rawRecipientId = selectedConversation.participantPhone || selectedConversation.participants?.find((participant: string) => {
                        const normalizedParticipant = participant?.toString?.().replace(/\s+/g, '').replace(/\+/g, '');
                        const normalizedBusinessId = businessPhoneNumberIdForRecipient?.toString?.().replace(/\s+/g, '').replace(/\+/g, '');
                        // Return the participant that doesn't match the businessPhoneNumberId
                        return normalizedParticipant && normalizedParticipant !== normalizedBusinessId;
                      }) || selectedConversation.participants?.[1] || selectedConversation.participants?.[0] || selectedConversation._id;

                      // Format recipient phone number to include country code if missing
                      const formattedRecipientId = rawRecipientId?.startsWith('+')
                        ? rawRecipientId
                        : rawRecipientId?.startsWith('44')
                          ? `+${rawRecipientId}`
                          : `+44${rawRecipientId}`;

                      // Use the display phone number from activeWhatsappNumber (keep spaces to match WhatsApp API format)
                      const businessPhoneNumberId = activeWhatsappNumber;


                      // Preserve all contact details from Redux state
                      // These are set when the conversation is opened via FLIP_MESSAGE_MODAL
                      const contactName = selectedConversation.contactName || selectedConversation.contactInfo?.name || selectedConversation.contactInfo?.profileName || selectedConversation.name || selectedConversation.participantPhone || 'Unknown Contact';
                      const contactInfo = selectedConversation.contactInfo || (selectedConversation.contactName ? {
                        name: selectedConversation.contactName,
                        profileName: selectedConversation.contactName,
                        profilePicture: selectedConversation.contactProfilePicture
                      } : null);

                      console.log('📱 Messages.tsx - Passing contact details to WhatsAppChatWrapper:', {
                        conversationId,
                        recipientId: formattedRecipientId,
                        contactName,
                        contactInfo,
                        participantPhone: selectedConversation.participantPhone,
                        hasContactInfo: !!contactInfo,
                        contactInfoKeys: contactInfo ? Object.keys(contactInfo) : []
                      });

                      return (
                        <WhatsAppChatWrapper
                          conversationId={conversationId}
                          recipientId={formattedRecipientId}
                          recipientName={contactName}
                          businessPhoneNumberId={businessPhoneNumberId}
                          contactInfo={contactInfo}
                          contactName={contactName}
                          contactProfilePicture={selectedConversation.contactProfilePicture || selectedConversation.contactInfo?.profilePicture}
                          participantPhone={selectedConversation.participantPhone}
                        />
                      );
                    })()
                  ) : (
                    <ChatContainer socket={socket} selectedConversation={selectedConversation} />
                  )
                )}
              </View>
            </View>
          </MotiView>
        </View>
      </MessageListProvider>
    </View>
  );
};

const mapStateToProps = function (state: DefaultRootState) {
  return {
    messages: state.modals.messageModal.messageContent?.messages,
    messageFlip: state.modals.messageModal.flipped,
    convoId: state.modals.messageModal.messageContent?.conversationId,
    statusBarHeight: state.UI.static.statusBarHeight
  }
}

export default connect(mapStateToProps)(Messages);